import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { thumbHashToDataURL } from "thumbhash";
import { cn } from "../../cn";
import Icon from "../../foundations/Icons/Icon";

type ImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  thumbHash?: string;
};

export default function LazyImage({ thumbHash, ...props }: ImageProps) {
  const src = props.src?.trim() ? props.src : undefined;
  const img = useRef<HTMLImageElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useLayoutEffect(() => {
    // Don't show thumbhash if image is already in cache
    setLoaded(img.current?.complete ?? false);
  }, []);

  const thumbHashSrc = useMemo(() => {
    if (!thumbHash) {
      return undefined;
    }
    const byteArray = atob(thumbHash)
      .split("")
      .map((c) => c.charCodeAt(0));
    return thumbHashToDataURL(byteArray);
  }, [thumbHash]);

  return (
    <>
      {(hasError || !src) && (
        <div
          className={cn(
            "flex items-center justify-center bg-grey-5",
            props.className,
          )}
        >
          <Icon icon="upload_error" className="size-14 bg-grey-8" />
        </div>
      )}
      {src && thumbHashSrc && (
        <img
          {...props}
          src={thumbHashSrc}
          className={cn(
            props.className,
            loaded ? "invisible w-0 h-0" : "visible",
          )}
        />
      )}
      {src && (
        <img
          {...props}
          src={src}
          ref={img}
          loading="lazy"
          onLoadCapture={() => setLoaded(true)}
          onError={() => setHasError(true)}
          className={cn(
            props.className,
            loaded ? "visible" : "invisible w-0 h-0",
          )}
        />
      )}
    </>
  );
}
